'use client';

import { useState, useEffect, useCallback } from 'react';
import { createBrowserClient } from '@/lib/supabase-clients';

export interface RecentExport {
  id: string;
  moment_id: string | null;
  moment_title: string;
  post_id: string;
  post_title: string;
  export_url: string | null;
  supabase_storage_url: string | null;
  share_token: string | null;
  created_at: string;
  completed_at: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  quality: string;
  isActive: boolean;
  isFailed: boolean;
  isFullVideoExport: boolean;
  errorMessage?: string;
}

interface UseRecentExportsResult {
  exports: RecentExport[];
  activeExports: RecentExport[];
  completedExports: RecentExport[];
  failedExports: RecentExport[];
  activeCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  dismissExport: (exportId: string) => Promise<boolean>;
}

/**
 * Fetch recent exports for the bell notification
 * @param domainId - Active domain ID to filter exports (domain-based access)
 * @param limit - Maximum number of exports to fetch
 */
export function useRecentExports(domainId?: string, limit = 10): UseRecentExportsResult {
  const [exports, setExports] = useState<RecentExport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const supabase = createBrowserClient();
      const { data: authData } = await supabase.auth.getUser();

      if (!authData.user) {
        setLoading(false);
        return;
      }

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', authData.user.id)
        .single();

      if (profileError || !profile) {
        console.error('[useRecentExports] Profile error:', profileError);
        setLoading(false);
        return;
      }

      // Fetch recent exports for the active domain (team-shared)
      // If no domainId provided, fall back to user's own exports
      // Exclude 'dismissed' status - those are hidden by user
      let query = supabase
        .from('moment_exports')
        .select(`
          id,
          moment_id,
          post_id,
          domain_id,
          user_id,
          title,
          export_url,
          supabase_storage_url,
          share_token,
          created_at,
          completed_at,
          status,
          quality,
          metadata,
          error_message
        `)
        .in('status', ['pending', 'processing', 'completed', 'failed'])
        .order('created_at', { ascending: false })
        .limit(limit);

      // Filter by domain (team access) or user (fallback)
      if (domainId) {
        query = query.eq('domain_id', domainId);
      } else {
        query = query.eq('user_id', profile.id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('[useRecentExports] Fetch error:', fetchError);
        setError('Failed to load recent exports');
        setLoading(false);
        return;
      }

      if (data) {
        const transformedExports: RecentExport[] = data.map((exp: any) => {
          const isActive = exp.status === 'pending' || exp.status === 'processing';
          const isFailed = exp.status === 'failed';
          const isFullVideoExport = exp.metadata?.isPostExport === true;

          return {
            id: exp.id,
            moment_id: exp.moment_id,
            moment_title: exp.title || (isFullVideoExport ? 'Full Video Export' : 'Untitled Moment'),
            post_id: exp.post_id,
            post_title: 'Video Export',
            export_url: exp.export_url,
            supabase_storage_url: exp.supabase_storage_url,
            share_token: exp.share_token,
            created_at: exp.created_at,
            completed_at: exp.completed_at,
            status: exp.status,
            quality: exp.quality || 'hd',
            isActive,
            isFailed,
            isFullVideoExport,
            errorMessage: exp.error_message
          };
        });

        setExports(transformedExports);
      }

      setLoading(false);
    } catch (err) {
      console.error('[useRecentExports] Unexpected error:', err);
      setError('An unexpected error occurred');
      setLoading(false);
    }
  }, [domainId, limit]);

  // Derive active, completed, and failed exports
  const activeExports = exports.filter(exp => exp.isActive);
  const completedExports = exports.filter(exp => !exp.isActive && !exp.isFailed);
  const failedExports = exports.filter(exp => exp.isFailed);
  const activeCount = activeExports.length;

  // Dismiss an export (hide it from the list)
  const dismissExport = useCallback(async (exportId: string): Promise<boolean> => {
    if (!domainId) {
      console.error('[useRecentExports] Cannot dismiss without domainId');
      return false;
    }

    try {
      const response = await fetch('/api/moments/dismiss-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exportId, domainId })
      });

      if (response.ok) {
        // Remove from local state immediately
        setExports(prev => prev.filter(exp => exp.id !== exportId));
        return true;
      }
      return false;
    } catch (err) {
      console.error('[useRecentExports] Dismiss error:', err);
      return false;
    }
  }, [domainId]);

  useEffect(() => {
    fetchExports();

    // Poll faster (10s) when there are active exports, slower (30s) otherwise
    const pollInterval = activeCount > 0 ? 10000 : 30000;
    const interval = setInterval(fetchExports, pollInterval);
    return () => clearInterval(interval);
  }, [fetchExports, activeCount]);

  return {
    exports,
    activeExports,
    completedExports,
    failedExports,
    activeCount,
    loading,
    error,
    refresh: fetchExports,
    dismissExport
  };
}
